import { Employee } from '../contexts/AppContext.tsx';
import { ActualSalesDebugLogger, PerformanceTracker, DataValidator } from '../utils/debugUtils.ts';
import {
  selectTargetEmployees,
  buildEmployeeTeamMap,
  getTeamForAssist,
  buildAssistTeamKey
} from './teamMonthlyDataService.ts';
import {
  attributeBlankAssistReceipts,
  buildDayAssistResolver,
  flattenAttributionDoc,
  splitByWeights,
  type ReceiptRow,
} from '../utils/salesAttribution.ts';
import { loadSalesAttributionDoc } from './firebase/salesAttributionFirebaseService.ts';
import { WorkingDaysStatistics } from '../types/teamPerformance.ts';
import { accumulateSourceMax, SourceMaxMap } from '../utils/monthlyAggregation.ts';


// 어시스트별 상세 데이터 타입
export interface AssistDetailData {
  receipts: number;
  expenses: number;
  profit: number;
  teamName: string;
  compositeKey?: string;
  /**
   * 금액 출처 — 'direct' 어시스트 이름으로 바로 귀속 / 'auto' 빈 어시스트 수납건 자동 배분 /
   * 'manual' 관리자 수납건 지정 / 'unassigned' 끝까지 못 정한 잔여. 없으면 direct.
   */
  attribution?: 'direct' | 'auto' | 'manual' | 'unassigned';
  /** auto 일 때 근거가 된 진료의사 */
  doctorName?: string;
}

/** 빈 어시스트 수납건 귀속 요약 — 화면 안내·검산용 */
export interface AttributionSummary {
  poolTotal: number;
  poolCount: number;
  autoTotal: number;
  manualTotal: number;
  /** 규칙으로 못 정한 수납건 + 수납건에 없는 옛 누적 잔재 */
  unassignedTotal: number;
  unassigned: { date: string; doctorName: string; amount: number; count: number; reason: string }[];
  /** 풀로 보낸 집계행의 옛 누적(Σ출처 MAX) − 수납건 풀 총액. 0이면 정합 */
  residualReceipts: number;
  /** 집계행이 없어 풀에 넣지 않은 수납건 */
  orphans: { date: string; assistName: string; amount: number; count: number }[];
}

// 실제매출 계산 결과 타입
export interface ActualSalesResult {
  teamActualSales: Record<string, number>;
  totalActualSales: number;
  assistDetails: Record<string, AssistDetailData>;
  calculatedAt: string;
  dataSource: 'teamCollection_based';
  isValidated: boolean;
  // 근무일 통계 (v2)
  workingDaysStats?: WorkingDaysStatistics;
  // 🛡️ 오귀속 의심 플래그: 진료의사 매핑 부재 상태에서 '미지정' 귀속 매출이 유의미하게 남은 결과
  // (캐시 저장·성과 저장·웹훅 전송 게이트용 — 화면 표시는 그대로 사용)
  suspectMisattribution?: boolean;
  /** 빈 어시스트 수납건 귀속 요약 (자동 배분·수동 지정·미지정 잔여) */
  attribution?: AttributionSummary;
}

// Firebase 데이터 로드 결과 타입 (내부용)
interface TeamCollectionLoadResult {
  data: any[];
  workingDaysStats: WorkingDaysStatistics;
  /** 그 달 수납건(차트번호 단위) — 빈 어시스트 귀속의 입력. 필요한 필드만 뽑는다. */
  receipts: ReceiptRow[];
}

/** 안전한 숫자 변환: NaN/Infinity/문자열 → 0 */
const safeNumber = (val: unknown): number => {
  const n = Number(val);
  return Number.isFinite(n) ? n : 0;
};

/**
 * 근무일 판정 — 일별 필드만 사용한다.
 * 포함: 그날 실수납(amount>0)이 있거나, 실명 어시스트의 진료 건수(count>0)가 있는 행.
 *   - amount는 일별 실수납(총진료비-할인-미수금) → 전액 미수금 진료일은 amount=0이지만 count>0으로 포함.
 *   - 미등록 신규 직원도 assistName엔 실명이 찍히므로 포함(isUnassignedEmployee 플래그는 명단 미매칭이면
 *     활동 무관하게 true라 판정에 쓰지 않는다).
 * 제외: 빈 플레이스홀더 행(assistName이 리터럴 '미지정', 엑셀에 어시스트명 자체가 없던 행)의 count,
 *   재무거래 합성 행(amount·count 필드 없음 → 재무전용일 포함 여부는 미결 도메인 결정),
 *   레거시 이월 행(count=0, 월누적 monthlyReceipt만 있음 — 월누적 필드는 일별 신호가 아니므로 배제).
 * ※ 한계: '미지정' 이름으로 귀속된 진료가 그날 실수납까지 0이면 놓친다(플레이스홀더와 구분 불가).
 */
export const isWorkingDayRow = (r: any): boolean =>
  (safeNumber(r?.amount) > 0) ||
  (safeNumber(r?.count) > 0 && r?.assistName !== '미지정');

/**
 * 🛡️ 오귀속 게이트 기준월(YYYY-MM): doctorTeamMapping {} 주입 사고가 발생한 월.
 * 이전 월(2026-01~05)은 매핑이 원래 부재({}가 역사적 정본)라 '매핑 복구' 탈출로가 없어,
 * 게이트를 적용하면 열람마다 재계산·저장 차단 루프 + 화면/DB 성과 이중 숫자가 된다.
 * (대안이던 '이전 저장 캐시와 미지정 비중 비교' 방식은 캐시 저장이 이미 차단된 월에는
 *  비교 대상 캐시가 없어 루프를 끊지 못하므로, 결정적인 기준월 상수 방식을 채택)
 */
const MISATTRIBUTION_GATE_BASE_MONTH = '2026-06';

/**
 * 실제매출 자동화 서비스 (teamCollection 기반)
 * DailyReportPage의 loadMonthlyTeamDataFromTeamCollection 로직 활용
 */
export class ActualSalesService {
  
  /**
   * 월별 팀 실제매출을 teamCollection compositeKey 기반으로 계산
   * @param yearMonth 계산할 년월 (YYYY-MM)
   * @param employees 직원 데이터 (어시스트 → 팀 매핑용)
   * @param budgetData 예산 데이터 (월별 직원 데이터 조회용)
   * @returns 팀별 실제매출 데이터
   */
  static async calculateMonthlyActualSales(
    yearMonth: string,
    employees: Employee[],
    budgetData?: any
  ): Promise<ActualSalesResult> {
    // ✅ 공통 서비스: 월별 저장 직원 데이터 우선 사용
    const targetEmployees = selectTargetEmployees(employees, budgetData || null, yearMonth);


    const startTime = performance.now();
    
    ActualSalesDebugLogger.service(`${yearMonth} compositeKey 기반 실제매출 계산 시작`);
    ActualSalesDebugLogger.service(`입력 파라미터 검증`, {
      yearMonth,
      employeeCount: targetEmployees?.length || 0,
      employees: targetEmployees?.map(emp => `${emp.name}(${emp.team})`).slice(0, 5)
    });


    PerformanceTracker.start('calculateMonthlyActualSales');

    try {
      // 1. Firebase에서 dailyReports 컬렉션의 teamCollection 데이터 로드
      ActualSalesDebugLogger.step(1, `Firebase teamCollection 데이터 로드 시작`);
      const { data: teamCollectionData, workingDaysStats, receipts: monthReceipts } = await this.loadTeamCollectionDataFromFirebase(yearMonth);
      ActualSalesDebugLogger.step(1, `Firebase teamCollection 데이터 로드 완료`, {
        dataLength: teamCollectionData.length,
        hasTeamRows: 0, // 팀 행 제거됨
        hasAssistRows: teamCollectionData.filter(item => item.isAssistRow).length,
        sampleData: teamCollectionData.slice(0, 3),
        workingDays: workingDaysStats.totalWorkingDays
      });

      // 2. compositeKey 기반 어시스트별 데이터 집계 (5단계 매핑 적용)
      ActualSalesDebugLogger.step(2, `compositeKey 기반 어시스트별 데이터 집계 시작 (5단계 매핑)`);
      const { assistDetails, teamWorkingDatesMap: resolvedTeamWorkingDatesMap, attribution } = await this.aggregateAssistDataFromTeamCollection(teamCollectionData, targetEmployees, budgetData, yearMonth, monthReceipts);
      ActualSalesDebugLogger.step(2, `어시스트별 집계 완료`, {
        assistCount: Object.keys(assistDetails).length,
        assistList: Object.entries(assistDetails).map(([name, data]) =>
          `${name}(${data.teamName}): ${data.profit.toLocaleString()}원`
        )
      });

      // 🆕 팀별 근무일 통계를 정확한 팀명 기반으로 재구축
      workingDaysStats.teamWorkingDays = Object.fromEntries(
        Array.from(resolvedTeamWorkingDatesMap.entries()).map(([team, dates]) => [team, dates.size])
      );
      workingDaysStats.teamWorkingDates = Object.fromEntries(
        Array.from(resolvedTeamWorkingDatesMap.entries()).map(([team, dates]) => [team, Array.from(dates).sort()])
      );

      ActualSalesDebugLogger.step(2, `팀별 근무일 통계 재구축 완료 (getTeamForAssist 기반)`, {
        teamWorkingDays: workingDaysStats.teamWorkingDays
      });

      // 3. 🔧 어시스트별 상세 데이터를 기반으로 팀별 실제매출 계산 (중복 제거된 데이터 사용)
      ActualSalesDebugLogger.step(3, `어시스트별 상세 데이터 기반 팀별 실제매출 계산 시작 (중복 제거됨)`);

      const { teamSales: teamActualSales, totalSales: totalActualSales } = this.calculateTeamSalesFromAssistDetails(assistDetails);
      ActualSalesDebugLogger.step(3, `팀별 실제매출 계산 완료 (중복 제거됨)`, {
        teamCount: Object.keys(teamActualSales).length,
        teamSales: Object.entries(teamActualSales).map(([team, sales]) => 
          `${team}: ${sales.toLocaleString()}원`
        ),
        totalActualSales: totalActualSales.toLocaleString()
      });

      const finalTeamActualSales = teamActualSales;
      const finalTotalActualSales = totalActualSales;

      
      // 데이터 유효성 검사
      ActualSalesDebugLogger.validation('최종 계산 결과 유효성 검사 시작');
      const isTeamSalesValid = DataValidator.validateTeamActualSales(finalTeamActualSales);
      const isAssistDetailsValid = DataValidator.validateAssistDetails(assistDetails);
      
      if (!isTeamSalesValid || !isAssistDetailsValid) {
        ActualSalesDebugLogger.error('데이터 유효성 검사 실패', {
          isTeamSalesValid,
          isAssistDetailsValid
        });
        throw new Error('계산된 데이터가 유효하지 않습니다');
      }
    
      // 🔧 FIX: NaN/Infinity 최종 검증 (C13 방어)
      const sanitizedTeamActualSales = Object.fromEntries(
        Object.entries(finalTeamActualSales).map(([team, sales]) => [team, safeNumber(sales)])
      );
      const sanitizedTotalActualSales = safeNumber(finalTotalActualSales);

      const result: ActualSalesResult = {
        teamActualSales: sanitizedTeamActualSales,
        totalActualSales: sanitizedTotalActualSales,
        assistDetails,
        calculatedAt: new Date().toISOString(),
        dataSource: 'teamCollection_based',
        isValidated: true,
        workingDaysStats,  // 🆕 근무일 통계 추가
        attribution        // 빈 어시스트 수납건 귀속 요약 (화면 안내·검산)
      };

      // 🛡️ 오귀속 의심 판정을 결과에 동봉 — 캐시 저장(saveActualSalesData)과
      // 성과 저장·웹훅(performRefresh)이 동일 판정을 공유한다.
      // budgetData 미전달 시 undefined 유지 → saveActualSalesData가 자체 budgetData로 재판정
      if (budgetData) {
        result.suspectMisattribution = this.isSuspectMisattribution(result, budgetData, yearMonth);
      }
      
      const endTime = performance.now();
      const executionTime = Math.round(endTime - startTime);
      
      // 🆕 Financial Transactions 통합 결과 검증 및 상세 로깅
      const financialTransactionAssists = Object.entries(assistDetails)
        .filter(([key, details]) => key.includes('-financial') || details.compositeKey?.includes('financial'))
        .map(([key, details]) => ({
          key,
          teamName: details.teamName,
          receipts: details.receipts,
          expenses: details.expenses,
          profit: details.profit
        }));

      const financialTransactionSummary = {
        count: financialTransactionAssists.length,
        totalReceipts: financialTransactionAssists.reduce((sum, assist) => sum + assist.receipts, 0),
        totalExpenses: financialTransactionAssists.reduce((sum, assist) => sum + assist.expenses, 0),
        totalProfit: financialTransactionAssists.reduce((sum, assist) => sum + assist.profit, 0),
        teamBreakdown: financialTransactionAssists.reduce((breakdown: Record<string, any>, assist) => {
          const team = assist.teamName;
          if (!breakdown[team]) {
            breakdown[team] = { count: 0, receipts: 0, expenses: 0, profit: 0 };
          }
          breakdown[team].count++;
          breakdown[team].receipts += assist.receipts;
          breakdown[team].expenses += assist.expenses;
          breakdown[team].profit += assist.profit;
          return breakdown;
        }, {})
      };

      ActualSalesDebugLogger.service(`${yearMonth} compositeKey 기반 계산 완료 + Financial Transactions 통합`, {
        팀수: Object.keys(finalTeamActualSales).length,
        총실제매출: finalTotalActualSales.toLocaleString(),
        어시스트수: Object.keys(assistDetails).length,
        실행시간: `${executionTime}ms`,
        데이터유효성: isTeamSalesValid && isAssistDetailsValid,
        // 🆕 Financial Transactions 상세 정보
        거래관리통합: {
          거래어시스트수: financialTransactionSummary.count,
          총거래수입: financialTransactionSummary.totalReceipts.toLocaleString() + '원',
          총거래지출: financialTransactionSummary.totalExpenses.toLocaleString() + '원',
          순거래이익: financialTransactionSummary.totalProfit.toLocaleString() + '원',
          팀별현황: Object.entries(financialTransactionSummary.teamBreakdown).map(([team, data]: [string, any]) =>
            `${team}: ${data.count}건, 순이익 ${data.profit.toLocaleString()}원`
          ),
          전체대비거래비율: finalTotalActualSales !== 0
            ? `${((financialTransactionSummary.totalProfit / finalTotalActualSales) * 100).toFixed(2)}%`
            : '0.00% (매출 없음)'
        }
      });
      
      PerformanceTracker.end('calculateMonthlyActualSales');
      ActualSalesDebugLogger.memory('계산 완료 후 메모리 상태');
    
      return result;
      
    } catch (error) {
      ActualSalesDebugLogger.error(`${yearMonth} compositeKey 기반 계산 실패`, error);
      PerformanceTracker.end('calculateMonthlyActualSales');
      throw error;
    }
  }
  
  /**
   * Firebase dailyReports 컬렉션에서 teamCollection 데이터 로드
   * 일계표의 loadMonthlyTeamDataFromTeamCollection 로직과 동일
   */
  private static async loadTeamCollectionDataFromFirebase(yearMonth: string): Promise<TeamCollectionLoadResult> {
    const loadStartTime = performance.now();

    ActualSalesDebugLogger.firebase(`${yearMonth} Firebase teamCollection 데이터 로드 시작`);
    PerformanceTracker.start('loadTeamCollectionDataFromFirebase');

    const { getDocs, collection, query, where } = await import('firebase/firestore');
    const { db } = await import('../firebase');

    const reportsCol = collection(db, 'dailyReports');
    let snapshot;
    try {
      // FIX-D: 서버사이드 필터링 — 해당 월의 문서만 로드 (문서 ID가 YYYY-MM-DD 형식)
      const startDate = `${yearMonth}-01`;
      const endDate = `${yearMonth}-32`; // 어떤 월이든 31일을 초과하지 않으므로 안전한 상한값
      const monthQuery = query(
        reportsCol,
        where('__name__', '>=', startDate),
        where('__name__', '<', endDate)
      );
      snapshot = await getDocs(monthQuery);
    } catch (firebaseError: any) {
      // 🔧 FIX-03: Firebase 에러 타입 구분 (permission-denied vs 네트워크 에러)
      const errorCode = firebaseError?.code || '';
      if (errorCode === 'permission-denied') {
        ActualSalesDebugLogger.error(`${yearMonth} Firebase 권한 오류: dailyReports 읽기 권한이 없습니다. view_daily_report 또는 view_performance 권한이 필요합니다.`, firebaseError);
      } else if (errorCode === 'unavailable') {
        ActualSalesDebugLogger.error(`${yearMonth} Firebase 네트워크 오류: Firestore 서비스에 연결할 수 없습니다.`, firebaseError);
      } else {
        ActualSalesDebugLogger.error(`${yearMonth} Firebase 알 수 없는 오류 (code: ${errorCode})`, firebaseError);
      }
      throw firebaseError;
    }

    ActualSalesDebugLogger.firebase(`Firebase 서버사이드 필터링 로드 완료 (FIX-D)`, {
      filteredDocs: snapshot.docs.length,
      targetMonth: yearMonth
    });

    // 🔑 복합키 기반 중복 방지 시스템 (일계표와 동일)
    const processedCompositeKeys = new Set<string>();
    const allTeamCollectionData: any[] = [];
    // 빈 어시스트 귀속 입력: 그 달 수납건(차트번호 단위). 필요한 필드만 뽑는다 (환자명은 쓰지 않는다).
    const monthReceipts: ReceiptRow[] = [];

    // 🆕 근무일 추적 시스템 (합집합 방식)
    const workingDatesSet = new Set<string>();  // 전체 근무일 (합집합)
    // 팀별 근무일은 aggregateAssistDataFromTeamCollection()에서 정확한 팀명 기반으로 구축

    let processedDocs = 0;
    let totalItems = 0;
    
    ActualSalesDebugLogger.firebase(`Firebase 로드 문서 수: ${snapshot.docs.length} (서버 필터링 적용)`);
    ActualSalesDebugLogger.firebase(`검색 대상 월: ${yearMonth}`);
    
    // 해당 월과 일치하는 문서 확인
    const matchingDocs = snapshot.docs.filter(doc => doc.id.startsWith(yearMonth));
    ActualSalesDebugLogger.firebase(`${yearMonth} 월과 일치하는 문서`, {
      count: matchingDocs.length,
      docIds: matchingDocs.map(doc => doc.id)
    });
    
    snapshot.docs.forEach(doc => {
      const docDate = doc.id; // 문서 ID가 날짜 (YYYY-MM-DD)
      
      // 해당 월의 날짜인지 확인 (YYYY-MM 부분 비교)
      if (docDate.startsWith(yearMonth)) {
        processedDocs++;
        const data = doc.data();
        
        ActualSalesDebugLogger.firebase(`${docDate} 문서 처리`, {
          hasTeamCollection: !!(data && data.teamCollection),
          teamCollectionLength: Array.isArray(data?.teamCollection) ? data.teamCollection.length : 'not array',
          sampleTeamData: data?.teamCollection?.slice(0, 3)
        });

        // 🔧 Financial Transactions 디버깅 강화
        ActualSalesDebugLogger.firebase(`${docDate} 전체 문서 구조 확인`, {
          hasData: !!data,
          dataKeys: data ? Object.keys(data) : 'no data',
          hasFinancialTransactions: !!(data && data.financialTransactions),
          financialTransactionsType: data?.financialTransactions ? typeof data.financialTransactions : 'undefined',
          financialTransactionsKeys: data?.financialTransactions ? Object.keys(data.financialTransactions) : 'no financialTransactions',
          hasFinancialItems: !!(data?.financialTransactions?.items),
          financialItemsType: data?.financialTransactions?.items ? typeof data.financialTransactions.items : 'undefined',
          financialItemsIsArray: Array.isArray(data?.financialTransactions?.items),
          financialItemsLength: Array.isArray(data?.financialTransactions?.items) ? data.financialTransactions.items.length : 'not array'
        });
        
        // 수납건 수집 — 빈 어시스트 귀속(salesAttribution)의 입력
        const receiptItems = Array.isArray(data?.receipts?.items) ? data.receipts.items : [];
        receiptItems.forEach((r: any) => {
          monthReceipts.push({
            date: docDate,
            chartNumber: String(r?.chartNumber ?? ''),
            assistName: (r?.assistName ?? '').toString(),
            doctorName: (r?.doctorName ?? '').toString(),
            amount: safeNumber(r?.amount),
          });
        });

        // teamCollection에서 월별 데이터 직접 추출
        if (data && data.teamCollection && Array.isArray(data.teamCollection)) {
          // 🆕 실제 진료 활동이 있는 날만 근무일로 카운트 (isWorkingDayRow 정의 참조)
          if (data.teamCollection.some(isWorkingDayRow)) {
            workingDatesSet.add(docDate);
          }

          data.teamCollection.forEach((teamItem: any) => {
            // 🔍 어시스트 개별 행과 팀 헤더 행 모두 처리 (compositeKey 필수)
            if (teamItem.compositeKey) {
              const compositeKey = teamItem.compositeKey;

              // 🚫 복합키 중복 확인 (동일한 복합키가 같은 날짜에 중복 처리되지 않도록)
              const dateCompositeKey = `${docDate}_${compositeKey}`;
              if (processedCompositeKeys.has(dateCompositeKey)) {
                ActualSalesDebugLogger.firebase(`중복 compositeKey 건너뛰기: ${dateCompositeKey}`);
                return; // 이미 처리된 복합키는 건너뛰기
              }
              processedCompositeKeys.add(dateCompositeKey);
              
              // 🏆 teamCollection 데이터를 그대로 보존하여 반환
              allTeamCollectionData.push({
                ...teamItem,
                docDate: docDate,
                monthlyReceipt: safeNumber(teamItem.monthlyReceipt),
                monthlyExpense: safeNumber(teamItem.monthlyExpense),
                monthlyProfit: safeNumber(teamItem.monthlyProfit)
              });
              
              totalItems++;
              
              // 상세 로깅 추가 (팀 헤더 제거됨)
              if (teamItem.isAssistRow) {
                ActualSalesDebugLogger.assistDetail(`어시스트[${compositeKey}] ${teamItem.assistName} → ${teamItem.team}: 월수익=${(teamItem.monthlyProfit || 0).toLocaleString()}원`);
              }
            }
          });
        }
        
        // 🆕 financialTransactions 데이터 처리 (거래 관리 데이터 - 개선됨)
        ActualSalesDebugLogger.firebase(`${docDate} financialTransactions 조건 체크`, {
          condition1_hasData: !!data,
          condition2_hasFinancialTransactions: !!(data && data.financialTransactions),
          condition3_hasItems: !!(data && data.financialTransactions && data.financialTransactions.items),
          condition4_isArray: !!(data && data.financialTransactions && data.financialTransactions.items && Array.isArray(data.financialTransactions.items)),
          finalCondition: !!(data && data.financialTransactions && data.financialTransactions.items && Array.isArray(data.financialTransactions.items))
        });

        if (data && data.financialTransactions && data.financialTransactions.items && Array.isArray(data.financialTransactions.items)) {
          ActualSalesDebugLogger.firebase(`✅ ${docDate} financialTransactions 처리 시작`, {
            itemCount: data.financialTransactions.items.length,
            docDate: docDate,
            message: '수입/지출 데이터를 실제매출 계산에 통합'
          });

          // 🔧 개선: 동일 날짜의 어시스트별 거래 데이터 집계
          const dailyFinancialMap = new Map<string, {
            assistName: string;
            assistTeam: string;
            totalIncome: number;
            totalExpense: number;
            transactionCount: number;
            transactions: any[];
          }>();

          // 1단계: 동일 어시스트의 거래들을 집계
          data.financialTransactions.items.forEach((transaction: any) => {
            const assistName = transaction.assistName || '미지정';
            const assistTeam = transaction.assistTeam || '미지정';
            const amount = safeNumber(transaction.amount);
            const transactionType = transaction.transactionType; // 'income' or 'expense'

            const assistKey = `${assistName}-${assistTeam}`;

            if (!dailyFinancialMap.has(assistKey)) {
              dailyFinancialMap.set(assistKey, {
                assistName: assistName,
                assistTeam: assistTeam,
                totalIncome: 0,
                totalExpense: 0,
                transactionCount: 0,
                transactions: []
              });
            }

            const assistData = dailyFinancialMap.get(assistKey)!;
            assistData.transactionCount++;
            assistData.transactions.push(transaction);

            if (transactionType === 'income') {
              assistData.totalIncome += amount;
            } else if (transactionType === 'expense') {
              assistData.totalExpense += amount;
            }

            ActualSalesDebugLogger.assistDetail(`거래 집계: ${assistName}(${assistTeam}) - ${transactionType}: ${amount.toLocaleString()}원`);
          });

          // 2단계: 집계된 데이터를 teamCollection 형태로 변환
          dailyFinancialMap.forEach((assistData, assistKey) => {
            const financialCompositeKey = `${assistData.assistName}-${assistData.assistTeam}-financial-${docDate}`;

            if (!processedCompositeKeys.has(financialCompositeKey)) {
              // 🔧 개선된 계산 로직
              const monthlyReceipt = safeNumber(assistData.totalIncome);
              const monthlyExpense = safeNumber(assistData.totalExpense);
              const monthlyProfit = monthlyReceipt - monthlyExpense;

              const financialTeamItem = {
                compositeKey: financialCompositeKey,
                assistName: assistData.assistName,
                team: assistData.assistTeam,
                teamName: assistData.assistTeam,
                monthlyReceipt: monthlyReceipt,
                monthlyExpense: monthlyExpense,
                monthlyProfit: monthlyProfit,
                isAssistRow: true,
                isFinancialTransaction: true,
                transactionCount: assistData.transactionCount,
                transactionDate: docDate,
                // 🆕 상세 거래 정보 포함
                financialDetails: {
                  totalIncome: assistData.totalIncome,
                  totalExpense: assistData.totalExpense,
                  transactionCount: assistData.transactionCount,
                  netProfit: monthlyProfit
                }
              };

              allTeamCollectionData.push(financialTeamItem);
              processedCompositeKeys.add(financialCompositeKey);
              totalItems++;

              ActualSalesDebugLogger.assistDetail(`💰 거래 통합[${financialCompositeKey}] ${assistData.assistName} → ${assistData.assistTeam}:`, {
                수입: `${monthlyReceipt.toLocaleString()}원`,
                지출: `${monthlyExpense.toLocaleString()}원`,
                순이익: `${monthlyProfit.toLocaleString()}원`,
                거래횟수: `${assistData.transactionCount}건`
              });
            }
          });

          ActualSalesDebugLogger.firebase(`${docDate} financialTransactions 처리 완료`, {
            assistCount: dailyFinancialMap.size,
            totalTransactions: data.financialTransactions.items.length,
            totalIncome: Array.from(dailyFinancialMap.values()).reduce((sum, data) => sum + data.totalIncome, 0),
            totalExpense: Array.from(dailyFinancialMap.values()).reduce((sum, data) => sum + data.totalExpense, 0)
          });
        } else {
          ActualSalesDebugLogger.firebase(`❌ ${docDate} financialTransactions 처리 건너뜀`, {
            reason: 'financialTransactions 조건문 실패',
            hasData: !!data,
            hasFinancialTransactions: !!(data && data.financialTransactions),
            hasItems: !!(data && data.financialTransactions && data.financialTransactions.items),
            isArray: !!(data && data.financialTransactions && data.financialTransactions.items && Array.isArray(data.financialTransactions.items))
          });
        }
      }
    });
    
    const loadEndTime = performance.now();
    const loadTime = Math.round(loadEndTime - loadStartTime);
    
    ActualSalesDebugLogger.firebase(`${yearMonth} teamCollection + financialTransactions 로드 완료`, {
      processedDocs,
      totalItems,
      compositeKeysCount: processedCompositeKeys.size,
      teamCollectionItems: allTeamCollectionData.filter(item => !item.isFinancialTransaction).length,
      financialTransactionItems: allTeamCollectionData.filter(item => item.isFinancialTransaction).length,
      sampleCompositeKeys: Array.from(processedCompositeKeys).slice(0, 10),
      로드시간: `${loadTime}ms`
    });
    
    PerformanceTracker.end('loadTeamCollectionDataFromFirebase');

    // 근무일 통계 생성 (팀별 근무일은 calculateMonthlyActualSales에서 재구축됨)
    const workingDaysStats: WorkingDaysStatistics = {
      totalWorkingDays: workingDatesSet.size,
      teamWorkingDays: {},
      workingDates: Array.from(workingDatesSet).sort(),
      teamWorkingDates: {}
    };

    ActualSalesDebugLogger.firebase(`${yearMonth} 근무일 통계`, {
      totalWorkingDays: workingDaysStats.totalWorkingDays,
      teamWorkingDays: workingDaysStats.teamWorkingDays
    });

    return {
      data: allTeamCollectionData,
      workingDaysStats,
      receipts: monthReceipts,
    };
  }
  
  /**
   * teamCollection 데이터에서 어시스트별 상세 데이터 집계
   * 🔧 개선: TeamCollectionService 5단계 매핑 로직 통합으로 정확한 팀 분류
   */
  private static async aggregateAssistDataFromTeamCollection(
    teamCollectionData: any[],
    employees: Employee[] = [],
    budgetData?: any,
    yearMonth?: string,
    receipts: ReceiptRow[] = []
  ): Promise<{ assistDetails: Record<string, AssistDetailData>; teamWorkingDatesMap: Map<string, Set<string>>; attribution: AttributionSummary }> {
    ActualSalesDebugLogger.assistDetail('어시스트별 상세 데이터 집계 시작 (팀별 분리)', {
      totalDataCount: teamCollectionData.length
    });

    PerformanceTracker.start('aggregateAssistDataFromTeamCollection');

    // ✅ 공통 서비스: 직원 팀 매핑 생성
    const employeeTeamMap = buildEmployeeTeamMap(employees);

    ActualSalesDebugLogger.assistDetail('직원 팀 매핑 생성 (공통 서비스)', {
      employeeCount: employees.length,
      sampleMappings: Object.entries(employeeTeamMap).slice(0, 5).map(([name, team]) => `${name} → ${team}`)
    });

    const assistDetails: Record<string, AssistDetailData> = {};
    // 🛡️ 버킷별 출처(원본 compositeKey)별 MAX 맵 — 총합 = Σ(출처별 MAX) 보장용
    const sourceMaxByBucket: Record<string, SourceMaxMap> = {};

    // ── 빈 어시스트 수납건 귀속 (정본: utils/salesAttribution.ts) ──
    //    어시스트 이름 → 팀 해석은 아래 루프와 같은 사슬(getTeamForAssist)을 날짜별로 미리 돌려 둔다.
    //    같은 해석기를 쓰므로 "루프에서 풀로 보내는 행" = "귀속 함수가 풀로 본 수납건" 이 보장된다.
    //    (이름 키를 compositeKey 앞부분·raw assistName 둘 다 등록, 자리표시 행이 풀린 팀을 덮지 못함 — 공용 빌더)
    const dayResolver = buildDayAssistResolver(teamCollectionData, (row: any, nameFromKey) =>
      getTeamForAssist(nameFromKey, employeeTeamMap, row.cashierName || '', undefined, undefined, row.team)
    );
    // 지정 문서는 fail-closed: 읽기 실패를 '지정 없음'으로 처리하면 관리자 지정이 조용히 무시된 채
    // 자동 배분 결과가 캐시·성과·웹훅으로 나간다 (Codex 재심 P0-3). 실패하면 계산 자체를 멈춘다.
    const attributionDoc = yearMonth ? await loadSalesAttributionDoc(yearMonth) : null;
    const { receiptOverrides, doctorTeamMapping } = flattenAttributionDoc(attributionDoc);
    const attribution = attributeBlankAssistReceipts({
      receipts,
      resolveAssistTeam: dayResolver,
      receiptOverrides,
      doctorTeamMapping,
    });
    // 풀로 보낸 집계행이 옛 규칙으로 가지고 있던 금액 = 출처별 월 누적 MAX 의 합.
    // 수납건 풀 총액과 다르면(하루 재업로드로 누적이 stale 등) 그 차이는 사라지면 안 된다 →
    // '미지정' 잔재 버킷으로 남긴다 (Codex 재심 P0-2). 지출도 같은 방식.
    const poolLegacySources: SourceMaxMap = {};
    const teamWorkingDatesMap = new Map<string, Set<string>>();  // 🆕 정확한 팀명 기반 근무일 추적
    let processedCount = 0;
    let skippedCount = 0;
    let overwriteCount = 0;


    for (let index = 0; index < teamCollectionData.length; index++) {
      const item = teamCollectionData[index];
      // 어시스트 개별 행만 처리 (팀 헤더 행 제외)
      if (item.isAssistRow && item.assistName) {
        // 🔧 compositeKey에서 어시스트명 추출 (미지정 데이터 팀별 구분용)
        let assistName = item.assistName;
        if (item.compositeKey && item.compositeKey.includes('-')) {
          const [keyAssistName] = item.compositeKey.split('-');
          if (keyAssistName) {
            assistName = keyAssistName;
          }
        }
        
        // ✅ 공통 서비스: 팀 매핑 (1순위, 1.5순위, 2순위, 2.5순위, 4순위, 5순위)
        const cashierName = (item as any).cashierName || '';
        const isFinancialTransaction = item.isFinancialTransaction || false;

        // 공통 서비스 getTeamForAssist 사용 (TeamCollectionService 제외)
        let teamName = getTeamForAssist(
          assistName,
          employeeTeamMap,
          cashierName,
          isFinancialTransaction ? item.assistTeam : undefined,  // 2순위: Financial만
          isFinancialTransaction ? item.teamName : undefined,    // 2.5순위: Financial만
          item.team                                               // 4순위
        );

        ActualSalesDebugLogger.assistDetail(`✅ 공통 서비스 팀 매핑: ${assistName} → ${teamName}`, {
          source: 'getTeamForAssist',
          isFinancialTransaction,
          cashierName: cashierName || '없음'
        });

        // 근무일 추적은 풀 여부와 무관하게 먼저 (기존 정의 유지 — 금액 귀속과 별개)
        // 🆕 팀별 근무일 추적 (getTeamForAssist로 해석된 정확한 팀명 사용)
        //    전체 근무일과 동일 정의(isWorkingDayRow 정의 참조)
        if (item.docDate && teamName && isWorkingDayRow(item)) {
          if (!teamWorkingDatesMap.has(teamName)) {
            teamWorkingDatesMap.set(teamName, new Set<string>());
          }
          teamWorkingDatesMap.get(teamName)!.add(item.docDate);
        }

        // 🔀 풀 행: 어시스트가 비었거나 명단·엑셀 팀칸으로도 팀을 못 정한 행.
        //    금액은 위에서 수납건 단위로 귀속됐으므로 여기서는 집계하지 않는다.
        //    (이전에는 의사 월 매핑(mapItemToTeamPublic)으로만 구제했고, 매핑이 없는 달은 통째로 미지정이었다)
        if (teamName === '미지정' && !isFinancialTransaction) {
          // 해석기가 이 (날짜, 이름)을 풀로 보는 경우에만 건너뛴다. 같은 날 같은 이름의 다른 행이 팀을
          // 풀었으면(해석기 = 그 팀) 이 행의 수납건은 풀이 아니므로, 옛 규칙대로 '미지정' 버킷에 집계해
          // 돈이 사라지지 않게 한다 (Codex 재심 P0-1).
          const poolView = dayResolver(assistName, item.docDate || '');
          if (poolView === '미지정') {
            accumulateSourceMax(poolLegacySources, item.compositeKey || `${assistName}-미지정`, safeNumber(item.monthlyReceipt), safeNumber(item.monthlyExpense));
            processedCount++;
            continue;
          }
        }

        // 월별 누적 데이터 사용
        const monthlyReceipt = safeNumber(item.monthlyReceipt);
        const monthlyExpense = safeNumber(item.monthlyExpense);
        const monthlyProfit = safeNumber(item.monthlyProfit);

        // ✅ compositeKey 리매핑 감지 (DailyReportPage와 동일 패턴)
        if (item.compositeKey && item.compositeKey.includes('-')) {
          const [storedAssistName, storedTeamName] = item.compositeKey.split('-');
          const currentCompositeKey = `${assistName}-${teamName}`;

          if (storedTeamName !== teamName) {
            ActualSalesDebugLogger.assistDetail(`🔄 compositeKey 리매핑 감지:`, {
              stored: item.compositeKey,
              current: currentCompositeKey,
              assistName,
              storedTeam: storedTeamName,
              currentTeam: teamName,
              changed: true,
              source: 'employeeTeamMap'
            });
          }
        }

        // ✅ 공통 서비스: 어시스트-팀 키 생성 (Financial은 -financial 접미사)
        const isFinancial = !!(item.isFinancialTransaction && item.compositeKey);
        const assistTeamKey = buildAssistTeamKey(assistName, teamName, isFinancial);
        
        /**
         * 🔍 데이터 집계 로직 - 두 데이터 타입의 저장 구조가 다르므로 다른 집계 방식 사용
         *
         * [Financial Transactions] (-financial 접미사)
         * - compositeKey 형식: "${assistName}-${assistTeam}-financial-${docDate}"
         * - 날짜가 키에 포함 → 각 날짜별로 별도 항목 생성
         * - monthlyReceipt = 해당 날짜의 거래 합계 (일별 값)
         * - 집계 방식: += 합산
         * - 예: 12/01(100) + 12/10(150) + 12/20(120) = 370만
         *
         * [일반 teamCollection]
         * - compositeKey 형식: "${assistName}-${teamName}" (날짜 미포함)
         * - 같은 어시스트는 같은 키 사용
         * - monthlyReceipt = 해당 날짜까지의 월 누적값
         * - 집계 방식: Math.max (가장 최신 = 가장 큰 누적값)
         * - 예: max(12/01:100만, 12/10:250만, 12/20:450만) = 450만
         */
        if (assistDetails[assistTeamKey]) {
          const existing = assistDetails[assistTeamKey];
          const isFinancial = assistTeamKey.includes('-financial');

          if (isFinancial) {
            // ✅ Financial: 날짜별 개별 거래이므로 합산 (+=)
            existing.receipts += monthlyReceipt;
            existing.expenses += monthlyExpense;
            existing.profit = existing.receipts - existing.expenses;

            if (monthlyReceipt > 0 || monthlyExpense > 0) {
              overwriteCount++;
              ActualSalesDebugLogger.assistDetail(`➕ Financial 데이터 누적: ${assistTeamKey}`, {
                추가값: { receipts: monthlyReceipt.toLocaleString(), expenses: monthlyExpense.toLocaleString() },
                누적합계: { receipts: existing.receipts.toLocaleString(), expenses: existing.expenses.toLocaleString(), profit: existing.profit.toLocaleString() }
              });
            }
          } else {
            // 📊 일반 teamCollection: 같은 출처(원본 compositeKey)는 월 누적값이므로 MAX,
            //    다른 출처는 SUM — 출처별 MAX 맵으로 총합 = Σ(출처별 MAX) 보장
            //    ('직전 키 1개 기억' 방식은 키가 A→B→A로 교차 도착 시 SUM 재발화로 이중계상)
            const sources = sourceMaxByBucket[assistTeamKey] || (sourceMaxByBucket[assistTeamKey] = {});
            const delta = accumulateSourceMax(sources, item.compositeKey || '', monthlyReceipt, monthlyExpense);

            if (delta.receipts > 0 || delta.expenses > 0) {
              overwriteCount++;
              ActualSalesDebugLogger.assistDetail(`📈 출처별 MAX 반영: ${assistTeamKey}`, {
                출처compositeKey: item.compositeKey,
                증가분: { receipts: delta.receipts.toLocaleString(), expenses: delta.expenses.toLocaleString() },
                누적합계: { receipts: (existing.receipts + delta.receipts).toLocaleString(), expenses: (existing.expenses + delta.expenses).toLocaleString() }
              });
            }

            existing.receipts += delta.receipts;
            existing.expenses += delta.expenses;
            existing.profit = existing.receipts - existing.expenses;
          }
        } else {
          // 처음 보는 어시스트 - 새로 추가 (비Financial은 출처별 MAX 맵에 기준값 등록)
          if (!isFinancial) {
            accumulateSourceMax((sourceMaxByBucket[assistTeamKey] = {}), item.compositeKey || '', monthlyReceipt, monthlyExpense);
          }
          assistDetails[assistTeamKey] = {
            receipts: monthlyReceipt,
            expenses: monthlyExpense,
            profit: monthlyProfit,
            teamName: teamName,
            compositeKey: item.compositeKey
          };
        }


        processedCount++;
        
        ActualSalesDebugLogger.assistDetail(`어시스트 집계: ${assistTeamKey}`, {
          receipts: monthlyReceipt.toLocaleString(),
          expenses: monthlyExpense.toLocaleString(),
          profit: monthlyProfit.toLocaleString(),
          compositeKey: item.compositeKey,
          isTeamSeparated: assistName !== assistTeamKey
        });
      } else {
        skippedCount++;
        ActualSalesDebugLogger.assistDetail(`어시스트가 아닌 데이터 건너뛰기`, {
          index,
          isAssistRow: item.isAssistRow,
          assistName: item.assistName,
          team: item.team
        });
      }
    }
    
    // ── 귀속 결과 주입: 자동 배분·수동 지정·미지정 잔여를 출처 태그와 함께 버킷으로 ──
    const legacyPoolReceipts = Object.values(poolLegacySources).reduce((x, v) => x + v.receipts, 0);
    const legacyPoolExpenses = Object.values(poolLegacySources).reduce((x, v) => x + v.expenses, 0);
    const residualReceipts = legacyPoolReceipts - attribution.poolTotal; // 수납건에 없는 누적 잔재 (0이면 정합)
    {
      const teamShare: Record<string, number> = {};
      Object.entries(attribution.autoByTeam).forEach(([team, byDoctor]) => {
        Object.entries(byDoctor).forEach(([doctor, amount]) => {
          if (amount === 0) return;
          const k = `자동 배분·${doctor}(${team})`;
          const prev = assistDetails[k];
          assistDetails[k] = {
            receipts: (prev?.receipts || 0) + amount, expenses: prev?.expenses || 0,
            profit: (prev?.profit || 0) + amount, teamName: team, attribution: 'auto', doctorName: doctor,
          };
          teamShare[team] = (teamShare[team] || 0) + amount;
        });
      });
      Object.entries(attribution.manualByTeam).forEach(([team, amount]) => {
        if (amount === 0) return;
        const k = `수동 지정(${team})`;
        const prev = assistDetails[k];
        assistDetails[k] = {
          receipts: (prev?.receipts || 0) + amount, expenses: prev?.expenses || 0,
          profit: (prev?.profit || 0) + amount, teamName: team, attribution: 'manual',
        };
        teamShare[team] = (teamShare[team] || 0) + amount;
      });
      // 풀 지출은 팀별 수납 배분과 같은 비율로 원 단위 합 보존 배분, 배분이 없으면 미지정으로
      const expenseSplit = legacyPoolExpenses !== 0 ? splitByWeights(legacyPoolExpenses, Object.fromEntries(Object.entries(teamShare).filter(([, v]) => v > 0))) : {};
      let expenseLeft = legacyPoolExpenses - Object.values(expenseSplit).reduce((x, v) => x + v, 0);
      Object.entries(expenseSplit).forEach(([team, e]) => {
        // 그 팀의 자동·수동 버킷들에 수납 비율로 다시 나눈다 — 한 버킷에 몰면 어시스트별 지출·이익이 틀린다
        const keys = Object.keys(assistDetails).filter(k =>
          assistDetails[k].teamName === team && (assistDetails[k].attribution === 'auto' || assistDetails[k].attribution === 'manual'));
        if (keys.length === 0) { expenseLeft += e; return; }
        const weights = Object.fromEntries(keys.map(k => [k, assistDetails[k].receipts]));
        const perBucket = splitByWeights(e, weights);
        const assigned = Object.values(perBucket).reduce((x, v) => x + v, 0);
        Object.entries(perBucket).forEach(([k, v]) => { assistDetails[k].expenses += v; assistDetails[k].profit -= v; });
        expenseLeft += e - assigned; // 가중치가 전부 0이면 배분되지 않은 만큼 미지정으로
      });
      // 미지정 잔여 = 규칙으로 못 정한 수납건 + 수납건에 없는 누적 잔재 + 배분 못 한 지출. 음수(환불)도 남긴다.
      //
      // ⚠️ 키는 buildAssistTeamKey('미지정','미지정') === '미지정(미지정)' 와 겹치면 안 된다.
      //    본 루프에서 '해석기가 팀을 푼 미지정 행'(P0-1 보존 경로)이 그 키로 이미 집계돼 있을 수 있고,
      //    대입하면 그 금액이 통째로 사라진다. 전용 키를 쓰고, 혹시 같은 버킷이 있으면 더한다.
      const unassignedReceipts = attribution.unassignedTotal + residualReceipts;
      if (unassignedReceipts !== 0 || expenseLeft !== 0) {
        const key = '미지정(규칙 미해결)';
        const prev = assistDetails[key];
        assistDetails[key] = {
          receipts: (prev?.receipts || 0) + unassignedReceipts,
          expenses: (prev?.expenses || 0) + expenseLeft,
          profit: (prev?.profit || 0) + unassignedReceipts - expenseLeft,
          teamName: '미지정', attribution: 'unassigned',
        };
      }
      ActualSalesDebugLogger.assistDetail('빈 어시스트 수납건 귀속', {
        풀총액: attribution.poolTotal.toLocaleString(), 옛누적합: legacyPoolReceipts.toLocaleString(), 잔재: residualReceipts.toLocaleString(),
        자동: attribution.autoTotal.toLocaleString(), 수동: attribution.manualTotal.toLocaleString(), 미지정: attribution.unassignedTotal.toLocaleString(),
        미지정사유: attribution.unassigned, 집계행없는수납건: attribution.orphans,
      });
    }

    // 🔍 미지정 팀 어시스트 별도 집계
    const unassignedAssists = Object.entries(assistDetails)
      .filter(([key, data]) => data.teamName === '미지정')
      .map(([key, data]) => ({ key, ...data }));
    
    ActualSalesDebugLogger.assistDetail('어시스트별 상세 데이터 집계 완료 (팀별 분리)', {
      processedCount,
      skippedCount,
      overwriteCount,
      totalAssists: Object.keys(assistDetails).length,
      unassignedCount: unassignedAssists.length,
      unassignedAssists: unassignedAssists.map(assist => 
        `${assist.key}: ${assist.profit.toLocaleString()}원`
      ),
      assists: Object.entries(assistDetails).map(([key, data]) => 
        `${key}: ${data.profit.toLocaleString()}원`
      )
    });
    
    PerformanceTracker.end('aggregateAssistDataFromTeamCollection');

    return {
      assistDetails,
      teamWorkingDatesMap,
      attribution: {
        poolTotal: attribution.poolTotal, poolCount: attribution.poolCount,
        autoTotal: attribution.autoTotal, manualTotal: attribution.manualTotal,
        unassignedTotal: attribution.unassignedTotal + residualReceipts, unassigned: attribution.unassigned,
        residualReceipts, orphans: attribution.orphans,
      },
    };
  }
  
  /**
   * 어시스트별 상세 데이터에서 팀별 실제매출 계산 (중복 제거된 데이터 기반)
   */
  private static calculateTeamSalesFromAssistDetails(
    assistDetails: Record<string, AssistDetailData>
  ): { teamSales: Record<string, number>; totalSales: number } {
    ActualSalesDebugLogger.teamAggregation('어시스트별 상세 데이터 기반 팀별 실제매출 계산 시작');


    const teamSales: Record<string, number> = {};
    let totalSales = 0;
    
    Object.entries(assistDetails).forEach(([assistName, details]) => {
      const teamName = details.teamName;
      const profit = details.profit;
      
      if (!teamSales[teamName]) {
        teamSales[teamName] = 0;
      }
      
      teamSales[teamName] += profit;
      totalSales += profit;
      
      ActualSalesDebugLogger.teamAggregation(`팀 집계: ${assistName} → ${teamName}`, {
        assistProfit: profit.toLocaleString(),
        teamCurrentTotal: teamSales[teamName].toLocaleString()
      });
    });
    
    ActualSalesDebugLogger.teamAggregation('팀별 실제매출 계산 완료', {
      teamCount: Object.keys(teamSales).length,
      totalSales: totalSales.toLocaleString(),
      teamSales: Object.entries(teamSales).map(([team, sales]) => 
        `${team}: ${sales.toLocaleString()}원`
      )
    });
    
    return { teamSales, totalSales };
  }
  
  /**
   * 지정된 년월의 dailyReports에서 가장 최근 업데이트 시간을 확인
   * @param yearMonth 확인할 년월 (YYYY-MM)
   * @returns 가장 최근 업데이트 시간 (Date 객체) 또는 null
   */
  public static async getLatestDailyReportUpdateTime(yearMonth: string): Promise<Date | null> {
    try {
      ActualSalesDebugLogger.cache(`${yearMonth} dailyReports 최신 업데이트 시간 확인 시작`);
      
      const { getDocs, collection, query, where, orderBy, limit } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      
      // yearMonth에 해당하는 dailyReports 문서들 중 가장 최근 updatedAt 조회
      const reportsCol = collection(db, 'dailyReports');
      const q = query(
        reportsCol,
        where('__name__', '>=', `${yearMonth}-01`),
        where('__name__', '<', `${yearMonth}-32`), // 31일을 넘어서는 날짜
        orderBy('updatedAt', 'desc'),
        limit(1)
      );
      
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        ActualSalesDebugLogger.cache(`${yearMonth} dailyReports에서 업데이트 시간을 찾을 수 없음`);
        return null;
      }
      
      const latestDoc = snapshot.docs[0];
      const updatedAt = latestDoc.data().updatedAt;
      
      // Firestore Timestamp를 Date로 변환
      let latestUpdateTime: Date;
      if (updatedAt && typeof updatedAt.toDate === 'function') {
        latestUpdateTime = updatedAt.toDate();
      } else if (updatedAt instanceof Date) {
        latestUpdateTime = updatedAt;
      } else {
        ActualSalesDebugLogger.cache(`${yearMonth} 유효하지 않은 updatedAt 형식:`, updatedAt);
        return null;
      }
      
      ActualSalesDebugLogger.cache(`${yearMonth} 가장 최근 dailyReports 업데이트 시간`, {
        docId: latestDoc.id,
        updatedAt: latestUpdateTime.toISOString(),
        timeDiffFromNow: `${Math.round((Date.now() - latestUpdateTime.getTime()) / (1000 * 60))}분 전`
      });
      
      return latestUpdateTime;
      
    } catch (error: any) {
      const errorCode = error?.code || '';
      ActualSalesDebugLogger.error(`${yearMonth} dailyReports 최신 업데이트 시간 확인 실패 (${errorCode})`, error);

      // permission-denied는 "데이터 없음"이 아닌 "접근 불가" → 에러 전파 (캐시 중독 방지)
      if (errorCode === 'permission-denied') {
        throw error;
      }

      // 네트워크 에러 등은 null 반환 (기존 동작 유지 — 캐시 폴백 허용)
      return null;
    }
  }
  
  /**
   * 캐시된 실제매출 데이터 로드 (캐시 유효성 검증 포함)
   * @param yearMonth 조회할 년월 (YYYY-MM)
   * @param budgetData 예산 데이터 객체
   * @returns 유효한 캐시 데이터 또는 null (재계산 필요)
   */
  static async loadCachedActualSales(
    yearMonth: string,
    budgetData: any
  ): Promise<ActualSalesResult | null> {

    ActualSalesDebugLogger.cache(`${yearMonth} 캐시된 실제매출 데이터 로드 시도 (유효성 검증 포함)`);
    
    // 1. 기본 데이터 존재 여부 확인 (🔧 FIX: budgetData.monthlyData 경로로 수정)
    if (!budgetData?.monthlyData?.[yearMonth]) {
      ActualSalesDebugLogger.cache(`${yearMonth} budgetData.monthlyData가 없습니다`);
      return null;
    }

    const monthData = budgetData.monthlyData[yearMonth];
    if (!monthData.actualSales || !monthData.actualSales.calculatedAt) {
      ActualSalesDebugLogger.cache(`${yearMonth} actualSales 캐시 데이터가 없습니다`);
      return null;
    }
    
    const cachedData = monthData.actualSales;
    const cacheCalculatedAt = new Date(cachedData.calculatedAt);

    // 🔒 캐시 자체 정합성 검증 (2026-06 사고 재발 방지):
    // (1) 빈 팀맵 캐시는 무효 — 재계산은 저렴·멱등이므로 무조건 재계산이 안전.
    //     단, '실적 없는 달'로 검증 저장된 캐시(validatedEmpty)는 유효 인정
    //     (빈캐시 저장 → 빈맵 무효화 → 재계산 무한 루프 방지)
    const cachedTeamMap = cachedData.teamActualSales || {};
    if (Object.keys(cachedTeamMap).length === 0 && cachedData.validatedEmpty !== true) {
      ActualSalesDebugLogger.cache(`${yearMonth} 캐시 무효: teamActualSales가 비어 있음 → 재계산`);
      return null;
    }
    // (2) 팀맵 합계와 총액 불일치(±1 초과)면 오염 캐시로 간주
    const cachedTeamSum = Object.values(cachedTeamMap).reduce((s: number, v: any) => s + (Number(v) || 0), 0);
    if (Math.abs(cachedTeamSum - (Number(cachedData.totalActualSales) || 0)) > 1) {
      ActualSalesDebugLogger.cache(`${yearMonth} 캐시 무효: 팀맵 합계(${cachedTeamSum})와 totalActualSales(${cachedData.totalActualSales}) 불일치 → 재계산`);
      return null;
    }

    ActualSalesDebugLogger.cache(`${yearMonth} 캐시된 데이터 발견 - 유효성 검증 시작`, {
      calculatedAt: cachedData.calculatedAt,
      teamCount: Object.keys(cachedData.teamActualSales || {}).length,
      totalSales: (cachedData.totalActualSales || 0).toLocaleString(),
      assistCount: Object.keys(cachedData.assistDetails || {}).length,
      dataSource: cachedData.dataSource,
      cacheAge: `${Math.round((Date.now() - cacheCalculatedAt.getTime()) / (1000 * 60))}분`
    });
    
    // 2. 24시간 만료 검사 (안전장치)
    const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    if (cacheCalculatedAt < twentyFourHoursAgo) {
      ActualSalesDebugLogger.cache(`${yearMonth} 캐시 만료: 24시간 초과`, {
        cacheAge: `${Math.round((Date.now() - cacheCalculatedAt.getTime()) / (1000 * 60 * 60))}시간`,
        maxAge: '24시간'
      });
      return null;
    }
    
    // 3. Firebase dailyReports와 비교하여 캐시 유효성 검사
    try {
      ActualSalesDebugLogger.cache(`${yearMonth} Firebase 최신 업데이트 시간과 비교 검증 시작`);

      let latestFirebaseUpdate: Date | null;
      try {
        latestFirebaseUpdate = await this.getLatestDailyReportUpdateTime(yearMonth);
      } catch (innerError: any) {
        if (innerError?.code === 'permission-denied') {
          ActualSalesDebugLogger.cache(`${yearMonth} Firebase 권한 오류 — 캐시 무효화, 재계산 필요`);
          return null; // 캐시 사용하지 않음 → 호출자가 재계산
        }
        // 네트워크 에러 등은 캐시 유지
        latestFirebaseUpdate = null;
      }

      if (latestFirebaseUpdate === null) {
        ActualSalesDebugLogger.cache(`${yearMonth} Firebase 업데이트 시간을 확인할 수 없음 - 캐시 사용`);
      } else {
        // Firebase 데이터가 캐시보다 최신이면 캐시 무효화
        if (latestFirebaseUpdate > cacheCalculatedAt) {
          const timeDiff = Math.round((latestFirebaseUpdate.getTime() - cacheCalculatedAt.getTime()) / (1000 * 60));
          ActualSalesDebugLogger.cache(`${yearMonth} 캐시 무효: Firebase 데이터가 더 최신`, {
            cacheTime: cacheCalculatedAt.toISOString(),
            firebaseTime: latestFirebaseUpdate.toISOString(),
            diffMinutes: `${timeDiff}분`,
            reason: 'Firebase 데이터 업데이트로 인한 캐시 무효화'
          });
          return null;
        } else {
          const timeDiff = Math.round((cacheCalculatedAt.getTime() - latestFirebaseUpdate.getTime()) / (1000 * 60));
          ActualSalesDebugLogger.cache(`${yearMonth} 캐시 유효: Firebase보다 최신 또는 동일`, {
            cacheTime: cacheCalculatedAt.toISOString(),
            firebaseTime: latestFirebaseUpdate.toISOString(),
            diffMinutes: `${timeDiff}분`,
            reason: '캐시가 Firebase 데이터보다 최신이므로 유효'
          });
        }
      }
      
    } catch (error) {
      ActualSalesDebugLogger.error(`${yearMonth} 캐시 유효성 검증 중 오류 발생 - 안전을 위해 캐시 무효화`, error);
      return null;
    }
    
    // 4. 모든 검증을 통과한 경우 캐시 데이터 반환
    ActualSalesDebugLogger.cache(`${yearMonth} 캐시 유효성 검증 완료 - 캐시 데이터 사용`);
    
    return {
      teamActualSales: cachedData.teamActualSales || {},
      totalActualSales: cachedData.totalActualSales || 0,
      assistDetails: cachedData.assistDetails || {},
      calculatedAt: cachedData.calculatedAt,
      dataSource: cachedData.dataSource || 'teamCollection_based',
      isValidated: true // 유효성 검증을 통과했음을 표시
    };
  }
  
  /**
   * 🛡️ 오귀속 의심 판정 (단일 정본): 진료의사 매핑이 비어 있는 상태({} 주입/소실 창)에서
   * 계산되어 '미지정' 귀속 매출이 총액의 5% 이상 남아 있으면 true.
   * - 사고 기준월(MISATTRIBUTION_GATE_BASE_MONTH) 이전 월은 게이트 미적용 (탈출로)
   * - 미지정 활동 검출은 receipts 외에 expenses·profit(음수 포함)도 검사
   * - 5% 문턱: 정당한 소액 미지정(매핑이 원래 없는 달의 잔여분)까지 막는 오탐 방지
   *   (비교값은 순액과 미지정 총활동 중 큰 값 — 수납·지출 상계 우회 방지)
   * 캐시 저장(saveActualSalesData)·성과 저장·웹훅(performRefresh)이 이 판정을 공유한다.
   */
  public static isSuspectMisattribution(
    actualSalesData: Pick<ActualSalesResult, 'teamActualSales' | 'totalActualSales' | 'assistDetails'>,
    budgetData: any,
    yearMonth: string
  ): boolean {
    // 사고 기준월 이전 과거 월은 게이트 미적용 (YYYY-MM 사전순 = 시간순 비교)
    // — 매핑이 원래 없던 달의 미지정 귀속은 오귀속이 아니라 역사적 정본
    if (yearMonth < MISATTRIBUTION_GATE_BASE_MONTH) {
      return false;
    }

    // (이전에는 옛 의사 월 매핑이 하나라도 있으면 게이트를 건너뛰었다. 귀속이 수납건 기준 정본으로
    //  바뀐 뒤로는 옛 매핑의 존재 여부가 미지정의 정당성과 무관하다 — 남은 미지정 자체로 판정한다.
    //  Codex 재심 P1-7)

    // '미지정' 귀속 활동 검출 (팀맵 값은 절대값 기준 — 음수 이익 오귀속도 검출)
    const unassignedTeamAmount = Math.abs(safeNumber((actualSalesData.teamActualSales as any)?.['미지정']));
    const hasUnassignedActivity =
      unassignedTeamAmount > 0 ||
      Object.values(actualSalesData.assistDetails || {}).some(
        (d: any) =>
          d?.teamName === '미지정' &&
          (safeNumber(d?.receipts) > 0 || safeNumber(d?.expenses) > 0 || safeNumber(d?.profit) !== 0)
      );
    if (!hasUnassignedActivity) {
      return false;
    }

    // 미지정 매출이 총액의 5% 미만이면 오탐(소액 잔여분)으로 보고 통과
    const totalAbs = Math.abs(safeNumber(actualSalesData.totalActualSales));
    if (totalAbs === 0) {
      return true; // 총액 0인데 미지정 활동만 존재 — 의심 유지
    }
    // 문턱 비교값: 순액(팀맵 절대값)과 미지정 총활동(assistDetails |수납|+|지출| 합) 중 큰 값
    // — 미지정 수납·지출이 상계돼 순액이 0에 가까워도 대규모 오귀속 구성을 놓치지 않도록
    const unassignedGrossActivity = Object.values(actualSalesData.assistDetails || {}).reduce(
      (sum: number, d: any) =>
        d?.teamName === '미지정'
          ? sum + Math.abs(safeNumber(d?.receipts)) + Math.abs(safeNumber(d?.expenses))
          : sum,
      0
    );
    return Math.max(unassignedTeamAmount, unassignedGrossActivity) >= totalAbs * 0.05;
  }

  /**
   * 실제매출 데이터를 Firebase에 저장
   */
  static async saveActualSalesData(
    yearMonth: string,
    actualSalesData: ActualSalesResult,
    budgetData: any,
    // 함수형 업데이터를 전달하므로 React.Dispatch<SetStateAction<...>> 호환 setter여야 함
    setBudgetData?: (data: any | ((prev: any) => any)) => void
  ): Promise<void> {
    ActualSalesDebugLogger.cache(`${yearMonth} 실제매출 데이터 저장 시작`, {
      teamCount: Object.keys(actualSalesData.teamActualSales).length,
      totalSales: actualSalesData.totalActualSales.toLocaleString(),
      assistCount: Object.keys(actualSalesData.assistDetails).length
    });
    
    PerformanceTracker.start('saveActualSalesData');
    
    try {
      const { saveBudgetMonthReplace } = await import('./firebase/budgetFirebaseService.ts');

      // 🛡️ 오귀속 캐시 방지 가드 (판정 정본: isSuspectMisattribution): 매핑 부재 상태에서
      // '미지정' 귀속 매출이 총액의 5% 이상이면 캐시를 저장하지 않는다.
      // 표시는 계산 결과 그대로, 다음 로드 때 신선한 매핑으로 재계산된다.
      const suspect = actualSalesData.suspectMisattribution ??
        this.isSuspectMisattribution(actualSalesData, budgetData, yearMonth);
      if (suspect) {
        ActualSalesDebugLogger.cache(`${yearMonth} 캐시 저장 스킵 — 진료의사 매핑 부재 상태에서 '미지정' 귀속 매출 존재(오귀속 의심)`, {
          totalSales: actualSalesData.totalActualSales.toLocaleString()
        });
        PerformanceTracker.end('saveActualSalesData');
        return;
      }

      // 🔖 실적 없는 달 마커: 빈 팀맵 캐시를 검증된 상태로 저장해
      // '빈캐시 저장 → 빈맵 무효화 → 재계산' 루프를 끊는다 (loadCachedActualSales에서 유효 인정)
      const isEmptyResult = Object.keys(actualSalesData.teamActualSales || {}).length === 0;

      // 저장·상태 반영 공용: 해당 월 actualSales 캐시 (6필드 스키마 + 빈 결과 마커)
      const newActualSalesCache = {
        teamActualSales: actualSalesData.teamActualSales,
        totalActualSales: actualSalesData.totalActualSales,
        assistDetails: actualSalesData.assistDetails,
        calculatedAt: actualSalesData.calculatedAt,
        dataSource: actualSalesData.dataSource,
        isValidated: actualSalesData.isValidated,
        ...(isEmptyResult ? { validatedEmpty: true } : {})
      };

      // per-month 저장: 해당 월의 actualSales 필드만 통째 교체(다른 달·다른 필드 무손상)
      await saveBudgetMonthReplace(yearMonth, {}, { actualSales: newActualSalesCache });

      if (setBudgetData) {
        // 🔧 함수형 패치: 진입 시점 budgetData 스냅샷으로 전체 상태를 교체하면
        // 네트워크 쓰기 대기 중 발생한 다른 in-memory 갱신이 롤백된다 —
        // 최신 상태(prev)에 해당 월의 actualSales 필드만 덧씌운다.
        setBudgetData((prev: any) => ({
          ...prev,
          monthlyData: {
            ...(prev?.monthlyData || {}),
            [yearMonth]: {
              ...(prev?.monthlyData?.[yearMonth] || {}),
              actualSales: newActualSalesCache
            }
          }
        }));
      }
      
      ActualSalesDebugLogger.cache(`${yearMonth} 실제매출 데이터 저장 완료`, {
        calculatedAt: actualSalesData.calculatedAt,
        dataSource: actualSalesData.dataSource
      });
      
      PerformanceTracker.end('saveActualSalesData');
      
    } catch (error) {
      ActualSalesDebugLogger.error(`${yearMonth} 실제매출 데이터 저장 실패`, error);
      PerformanceTracker.end('saveActualSalesData');
      throw error;
    }
  }
} 